import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  RadioChangeEvent,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Address, CreateInput } from "src/lib/address-autocomplete";
import { useUploadImage } from "services/image";
import { UploadRequestOption } from "rc-upload/lib/interface";
import { useRegisterUser } from "services/user";
import { IRegisterUserForm } from "types/user";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

const InputThaiAddress = CreateInput();

const { confirm } = Modal;

const RegisterForm = () => {
  const [form] = Form.useForm();
  const [userType, setUserType] = useState<number>(1); // user type 1 = ค้าส่ง
  const { uploadImageAsync } = useUploadImage();
  const navigation = useNavigate();
  const { registerUserAsync, isPending } = useRegisterUser();
  const [address, setAddress] = useState<Address>({
    district: "", // ตำบล tambol
    amphoe: "", // อำเภอ amphoe
    province: "", // จังหวัด changwat
    zipcode: "", // รหัสไปรษณีย์ postal code
  });
  const handleChangeAddress = (scope: string) => (value: string) => {
    setAddress((oldAddr: Address) => ({
      ...oldAddr,
      [scope]: value,
    }));
  };
  const handleSelectAddress = (address: Address) => {
    setAddress(address);
    form.setFieldsValue({
      province: address.province,
      district: address.amphoe,
      subdistrict: address.district,
      postCode: address.zipcode,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normFile = (e: any) => {
    console.log("Upload event:", e);
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };
  const showConfirm = (value: number) => {
    confirm({
      title: "กรุณายืนยันการเปลี่ยนประเภทของลูกค้า?",
      okText: "ตกลง",
      cancelText: "ยกเลิก",
      onOk() {
        setUserType(value);
        form.validateFields();
      },
      onCancel() {
        // setTempValue(selectedValue); // Revert to the previous value if canceled
      },
    });
  };

  const onChangeUserType = (e: RadioChangeEvent) => {
    const value = e.target.value;
    showConfirm(value);
  };

  const onSubmitForm = async (values: IRegisterUserForm) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...rest } = values;
      await registerUserAsync({
        ...rest,
        userTierId: userType,
        certificateImageKey: rest?.certificateImageKey?.[0].response.imageUrl,
        licenseImageKey: rest?.licenseImageKey?.[0].response.imageUrl,
      });

      navigation("/register-success", {
        state: { userTierId: userType },
      });
    } catch (e) {
      console.log(e);
      if (
        e instanceof AxiosError &&
        e.response?.data?.message === "Email already exists"
      ) {
        notification.error({
          message: "ไม่สามารถสมัครสมาชิกได้",
          description: "อีเมลนี้มีผู้ใช้งานแล้ว",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "เกิดข้อผิดพลาด",
          description: "กรุณาลองใหม่อีกครั้ง",
          placement: "topRight",
        });
      }
    }
  };

  const customRequest = (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    if (file instanceof File) {
      uploadImageAsync(file, {
        onSuccess: (data) => {
          onSuccess?.(data.data); // Notify AntD Upload component of success
        },
        onError: (error) => {
          onError?.(error); // Notify AntD Upload component of error
        },
      });
    }
  };

  return (
    <div className="flex flex-col justify-center">
      <Form
        layout="vertical"
        form={form}
        initialValues={{ userType: 1 }}
        onFinish={onSubmitForm}
      >
        <Form.Item
          required
          name="fullName"
          label="ชื่อ - นามสกุล"
          rules={[{ required: true, message: "กรุณากรอกชื่อ - นามสกุล" }]}
        >
          <Input size="large" placeholder="ชื่อ - นามสกุล" />
        </Form.Item>
        <Form.Item label="ประเภทของลูกค้า">
          <Radio.Group
            className="w-full"
            value={userType}
            onChange={onChangeUserType}
          >
            <div className="grid gap-4 grid-cols-2">
              <div className="border-double border-1 border-primary rounded-lg py-4 px-4 bg-white">
                <Radio value={1}>ซื้อส่ง</Radio>
              </div>
              <div className="border-double border-1 border-primary rounded-lg py-4 px-4 bg-white">
                <Radio value={5}>ซื้อปลีก</Radio>
              </div>
            </div>
          </Radio.Group>
          {userType === 5 && (
            <div className="mt-4 border-solid border-1 border-[#ff0000] rounded-lg py-4 px-4 bg-white">
              การสมัครสมาชิกประเภทซื้อปลีก จะไม่ต้องมีการขอเอกสารของ
              ใบประกอบวิชาชีพ และ ใบอนุญาต
              และลูกค้าปลีกจะไม่สามารถซื้อยาอันตรายได้
            </div>
          )}
        </Form.Item>
        <Form.Item
          required={userType === 1}
          name="storeName"
          label="ชื่อสถานที่ประกอบการ"
          rules={[
            {
              required: userType === 1,
              message: "กรุณากรอกชื่อสถานที่ประกอบการ",
            },
          ]}
        >
          <Input
            disabled={userType === 5}
            size="large"
            placeholder="ชื่อสถานที่ประกอบการ"
          />
        </Form.Item>
        <Form.Item
          required
          name="postCode"
          label="รหัสไปรษณีย์"
          rules={[{ required: true, message: "กรุณากรอกรหัสไปรษณีย์" }]}
        >
          <InputThaiAddress.Zipcode
            value={address["zipcode"]}
            onChange={handleChangeAddress("zipcode")}
            onSelect={handleSelectAddress}
            autoCompleteProps={{
              placeholder: "รหัสไปรษณีย์",
              rootClassName: "register-address-autocomplete",
            }}
          />
        </Form.Item>
        <Form.Item
          required
          name="province"
          label="จังหวัด"
          rules={[{ required: true, message: "จังหวัด" }]}
        >
          <InputThaiAddress.Province
            value={address["province"]}
            onChange={handleChangeAddress("province")}
            onSelect={handleSelectAddress}
            autoCompleteProps={{
              placeholder: "จังหวัด",
              rootClassName: "register-address-autocomplete",
            }}
          />
        </Form.Item>
        <Form.Item
          required
          name="district"
          label="เขต / อำเภอ"
          rules={[{ required: true, message: "กรุณากรอกเขต / อำเภอ" }]}
        >
          <InputThaiAddress.Amphoe
            value={address["amphoe"]}
            onChange={handleChangeAddress("amphoe")}
            onSelect={handleSelectAddress}
            autoCompleteProps={{
              placeholder: "อำเภอ",
              rootClassName: "register-address-autocomplete",
            }}
          />
        </Form.Item>
        <Form.Item
          required
          name="subdistrict"
          label="แขวง / ตำบล"
          rules={[{ required: true, message: "กรุณากรอกแขวง / ตำบล" }]}
        >
          <InputThaiAddress.District
            value={address["district"]}
            onChange={handleChangeAddress("district")}
            onSelect={handleSelectAddress}
            autoCompleteProps={{
              placeholder: "ตำบล",
              rootClassName: "register-address-autocomplete",
            }}
          />
        </Form.Item>
        <Form.Item
          required
          name="address"
          label="บ้านเลขที่ / ถนน"
          rules={[{ required: true, message: "กรุณากรอกบ้านเลขที่ / ถนน" }]}
        >
          <Input size="large" placeholder="บ้านเลขที่ / ถนน" />
        </Form.Item>
        <Form.Item
          required
          name="phoneNumber"
          label="เบอร์โทร"
          rules={[{ required: true, message: "กรุณากรอกเบอร์โทร" }]}
        >
          <Input size="large" placeholder="เบอร์โทร" />
        </Form.Item>
        <Form.Item
          required
          name="email"
          label="อีเมล"
          rules={[{ required: true, message: "กรุณากรอกอีเมล" }]}
        >
          <Input size="large" placeholder="อีเมล" />
        </Form.Item>
        <Form.Item
          required
          name="password"
          label="รหัสผ่าน"
          rules={[{ required: true, message: "กรุณากรอกรหัสผ่าน" }]}
        >
          <Input.Password size="large" placeholder="รหัสผ่าน" />
        </Form.Item>
        <Form.Item
          required
          name="confirmPassword"
          label="ยืนยันรหัสผ่าน"
          dependencies={["password"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: "กรถณากรอกยืนยันรหัสผ่าน",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The new password that you entered do not match!"),
                );
              },
            }),
          ]}
        >
          <Input.Password size="large" placeholder="ยืนยันรหัสผ่าน" />
        </Form.Item>
        {/* <Form.Item name="inviteCode" label="โค้ดผู้แนะนำ">
          <Input size="large" placeholder="โค้ดผู้แนะนำ" />
        </Form.Item> */}
        <Form.Item name="lineId" label="Line ID">
          <Input size="large" placeholder="Line ID" />
        </Form.Item>

        <Form.Item required label="ใบประกอบวิชาชีพ">
          <Form.Item
            name="certificateImageKey"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            noStyle
            rules={[
              {
                required: userType === 1,
                message: "กรุณาอัพโหลดรูปใบประกอบวิชาชีพ",
              },
            ]}
          >
            <Upload.Dragger
              name="certificateImageKey"
              disabled={userType === 5}
              customRequest={customRequest} // Use custom request for handling file uploads
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for a single or bulk upload.
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form.Item>

        <Form.Item
          name="certificateExpireDate"
          required
          label="วันหมดอายุใบประกอบวิชาชีพ"
          rules={[
            {
              required: userType === 1,
              message: "กรุณากรอกวันหมดอายุใบประกอบวิชาชีพ",
            },
          ]}
        >
          <DatePicker
            size="large"
            disabled={userType === 5}
            placeholder="วันหมดอายุของใบประกอบวิชาชีพ"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item required label="ใบอนุญาต">
          <Form.Item
            name="licenseImageKey"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            noStyle
            rules={[
              { required: userType === 1, message: "กรุณาอัพโหลดรูปใบอนุญาต" },
            ]}
          >
            <Upload.Dragger
              name="licenseImageKey"
              customRequest={customRequest} // Use custom request for handling file uploads
              disabled={userType === 5}
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for a single or bulk upload.
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form.Item>

        <Form.Item
          name="licenseExpireDate"
          required
          label="วันหมดอายุของใบอนุญาต"
          rules={[
            {
              required: userType === 1,
              message: "กรุณากรอกวันหมดอายุของใบอนุญาต",
            },
          ]}
        >
          <DatePicker
            size="large"
            disabled={userType === 5}
            style={{ width: "100%" }}
            placeholder="วันหมดอายุของใบอนุญาต"
          />
        </Form.Item>
        <div className="flex justify-between mt-12">
          <Button
            htmlType="reset"
            size="large"
            className="w-[160px]"
            disabled={isPending}
          >
            ล้างข้อมูล
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="w-[250px]"
            loading={isPending}
          >
            สมัครสมาชิก
          </Button>
        </div>
      </Form>
    </div>
  );
};
export default RegisterForm;
